import { useEffect } from 'react';
import { isServer } from '../../server/helpers/browserDetector';
// ##### 25-04-26 Komarov
import { uniLog } from '../../server/helpers/uniLog';
// ##### Komarov 2025-08-26
import getDev from '../helpers/getDevMode';

const getDevMode = () => getDev();
const devMode = getDevMode();

// ##### Komarov 2025-08-26
export const useAnimationFrame = (component, componentDevMode) => {
  useEffect(() => {
    if ((!componentDevMode && !devMode) || isServer) return;
    const raf = requestAnimationFrame(() => {
      const start = window?.techData?.clientStart ?? performance.timeOrigin;
      const diff = performance.now() - start;
      console.info(`[Client performance] ${component} visually rendered:`, diff, 'ms');
    });
    return () => cancelAnimationFrame(raf);
  }, [component, componentDevMode]);
  if (!componentDevMode && !devMode) return;
  // Server-side: фиксируем время рендера во время SSR вызова компонента
  if (isServer) {
    try {
      let durationMs = 0;
      // Try Node perf_hooks
      try {
        const req = typeof eval === 'function' ? eval('require') : undefined;
        if (req) {
          const nodePerfModule = req('perf_hooks');
          const nodePerformance = nodePerfModule && nodePerfModule.performance;
          if (nodePerformance && typeof nodePerformance.now === 'function') {
            durationMs = nodePerformance.now();
          }
        }
      } catch {
        // ignore
      }

      // Fallback to process.hrtime
      if (!durationMs && typeof process !== 'undefined' && typeof process.hrtime === 'function') {
        const hr = process.hrtime();
        if (Array.isArray(hr) && hr.length === 2) {
          durationMs = (hr[0] * 1000) + (hr[1] / 1e6);
        }
      }

      // Last resort: Date.now baseline
      if (!durationMs) {
        const g = typeof global !== 'undefined' ? global : {};
        if (!g.__serverStartMs) g.__serverStartMs = Date.now();
        durationMs = Date.now() - g.__serverStartMs;
      }
      // If we have per-request start time, compute delta from request start (lazy server-only require)
      let reqStart;
      try {
        const req = typeof eval === 'function' ? eval('require') : undefined;
        if (req) {
          const { getRequestStartMs } = req('../../server/helpers/requestTiming');
          reqStart = typeof getRequestStartMs === 'function' ? getRequestStartMs() : undefined;
        }
      } catch {
        // ignore
      }
      const outputMs = typeof reqStart === 'number' ? (Date.now() - reqStart) : durationMs;
      const label = typeof reqStart === 'number' ? '[Server performance] (since request start)' : '[Server performance]';
      console.info(`${label} ${component} rendered:`, outputMs, 'ms');
      /* try {
        uniLog(`[Server performance] ${component} rendered`, `${durationMs.toFixed(2)} ms`);
      } catch (err) {
        console.warn('[Server performance] uniLog failed:', err);
      } */
    } catch (error) {
      console.error('[Server performance] useAnimationFrame error:', error);
    }
  }
};

export const setTimeMark = (title, componentDevMode) => {
  if (!componentDevMode && !devMode) return;
  if (isServer) {
    try {
      // Try to get per-request start time
      let reqStart;
      try {
        const req = typeof eval === 'function' ? eval('require') : undefined;
        if (req) {
          const { getRequestStartMs } = req('../../server/helpers/requestTiming');
          reqStart = typeof getRequestStartMs === 'function' ? getRequestStartMs() : undefined;
        }
      } catch (error) {
        console.error('[Server performance] setTimeMark error:', error);
      }

      let markMs = 0;
      // Prefer perf_hooks for high-res
      try {
        const req = typeof eval === 'function' ? eval('require') : undefined;
        if (req) {
          const nodePerf = req('perf_hooks').performance;
          if (nodePerf && typeof nodePerf.now === 'function') {
            markMs = nodePerf.now();
          }
        }
      } catch (error) {
        console.error('[Server performance] setTimeMark error:', error);
      }

      if (!markMs && typeof process !== 'undefined' && typeof process.hrtime === 'function') {
        const hr = process.hrtime();
        if (Array.isArray(hr) && hr.length === 2) {
          markMs = (hr[0] * 1000) + (hr[1] / 1e6);
        }
      }
      if (!markMs) {
        const g = typeof global !== 'undefined' ? global : {};
        if (!g.__serverStartMs) g.__serverStartMs = Date.now();
        markMs = Date.now() - g.__serverStartMs;
      }

      const outputMs = typeof reqStart === 'number' ? (Date.now() - reqStart) : markMs;
      const label = typeof reqStart === 'number' ? '[Server performance] (since request start)' : '[Server performance]';
      console.info(`${label} ${title}`, outputMs, 'ms');
      return;
    } catch (error) {
      console.error('[Server performance] setTimeMark error:', error);
      return;
    }
  }

  try {
    const start = window?.techData?.clientStart ?? performance.timeOrigin;
    const diff = performance.now() + performance.timeOrigin - start;
    console.info(`[Client performance] ${title}`, diff, 'ms');
  } catch (error) {
    console.error('[Client performance] setTimeMark error:', error);
  }
};
// ##### Komarov 2025-08-20
// Server-side timing using Node's process.hrtime
const serverStartTimes = new Map();

function getServerTimestamp() {
  if (isServer) {
    const [seconds, nanoseconds] = process.hrtime();
    return `${seconds * 1000 + nanoseconds / 1e6}`; // Convert to milliseconds
  }
  return Date.now().toString();
}
/**
 * Gets current timestamp with microseconds
 * @returns {string} Timestamp in format "YYYY-MM-DD HH:mm:ss.microseconds"
 */
function getTimestamp(usePerformance = false) {
  if (usePerformance && !isServer) {
    // Use performance.now() for client-side high-resolution timing
    const now = performance.timeOrigin + performance.now();
    const date = new Date(now);
    const micros = Math.floor((now % 1000) * 1000); // Get microseconds from milliseconds

    return (
      date.toISOString().replace('T', ' ').replace('Z', '').slice(0, 19) +
      '.' +
      String(micros).padStart(6, '0')
    );
  }

  // Fallback to standard timing for server-side or when performance API is not available
  const date = new Date();
  const micros = date.getMilliseconds() * 1000; // Convert ms to μs

  return (
    date.toISOString().replace('T', ' ').replace('Z', '').slice(0, 19) +
    '.' +
    String(micros).padStart(6, '0')
  );
}

/**
 * Calculates time difference in seconds between two timestamps
 * @param {string} dateFinish - End timestamp in format "YYYY-MM-DD HH:mm:ss.microseconds"
 * @param {string} dateStart - Start timestamp in format "YYYY-MM-DD HH:mm:ss.microseconds"
 * @returns {number} Time difference in seconds with microsecond precision
 */
function getTimeDifferSeconds(dateFinish, dateStart) {
  // Extract time components
  const dF = {
    hours: parseInt(dateFinish.substr(11, 2)),
    minutes: parseInt(dateFinish.substr(14, 2)),
    seconds: parseFloat(dateFinish.substr(17)),
  };

  const dS = {
    hours: parseInt(dateStart.substr(11, 2)),
    minutes: parseInt(dateStart.substr(14, 2)),
    seconds: parseFloat(dateStart.substr(17)),
  };

  // Calculate total seconds for each timestamp
  const finishSeconds = dF.seconds + dF.minutes * 60 + dF.hours * 3600;
  const startSeconds = dS.seconds + dS.minutes * 60 + dS.hours * 3600;

  // Return the difference
  return finishSeconds - startSeconds;
}

// ##### 25-02-26 Komarov: Функция для запуска измерения (если хотим измерить одновременно несколько показателей, - нужно указать название для каждого измерения)
function startMeasuring(measureTitle = 'No title was given') {
  if (!devMode) return;
  setStart(measureTitle);
}

// ##### 25-08-18 Komarov: Функция для запуска измерения (если хотим измерить одновременно несколько показателей, - нужно указать название для каждого измерения)
function startMeasuringProd(measureTitle = 'No title was given') {
  if (!devMode) return;
  if (isServer) {
    const timestamp = getServerTimestamp();
    console.info(`[Server Performance] 🚀 Starting measurement: ${measureTitle} at ${timestamp}ms`);
    serverStartTimes.set(measureTitle, process.hrtime());
    return timestamp;
  } else {
    const timestamp = performance.now();
    console.info(`[Client Performance] 🚀 Starting measurement: ${measureTitle} at ${timestamp.toFixed(2)}ms`);
    try {
      setStart(measureTitle);
    } catch (error) {
      console.error(`[Performance] ❌ Error in startMeasuringProd for ${measureTitle}:`, error);
    }
    return timestamp;
  }
}

// ##### 25-02-26 Komarov: Функция для остановки измерения (если хотим измерить одновременно несколько показателей, - нужно указать название для каждого измерения, название в stopMeasuring должно совпадать с соответствующим названием для startMeasuring, например, для измерения показателя "hotels" вызываем эти функции так: startMeasuring("hotels") и stopMeasuring("hotels"))
function stopMeasuring(measureTitle = 'No title was given') {
  if (!devMode) return;
  setFinish(measureTitle);
}

// ##### 25-08-18 Komarov: Функция для остановки измерения (если хотим измерить одновременно несколько показателей, - нужно указать название для каждого измерения, название в stopMeasuring должно совпадать с соответствующим названием для startMeasuring, например, для измерения показателя "hotels" вызываем эти функции так: startMeasuring("hotels") и stopMeasuring("hotels"))
function stopMeasuringProd(measureTitle = 'No title was given') {
  if (!devMode) return;
  if (isServer) {
    const startTime = serverStartTimes.get(measureTitle);
    if (!startTime) {
      console.error(`[Server Performance] ❌ No start time found for: ${measureTitle}`);
      return null;
    }

    const [seconds, nanoseconds] = process.hrtime(startTime);
    const durationMs = (seconds * 1000) + (nanoseconds / 1e6);

    console.info(`[Server Performance] 🛑 Measurement complete: ${measureTitle} took ${durationMs.toFixed(2)}ms`);
    serverStartTimes.delete(measureTitle);
    return durationMs;
  } else {
    const timestamp = performance.now();
    console.info(`[Client Performance] 🛑 Stopping measurement: ${measureTitle} at ${timestamp.toFixed(2)}ms`);
    try {
      setFinish(measureTitle);
    } catch (error) {
      console.error(`[Performance] ❌ Error in stopMeasuringProd for ${measureTitle}:`, error);
    }
    return timestamp;
  }
}

function setStart(measureTitle) {
  if (!devMode) return;
  console.info(`⏱️ [setStart] Starting measurement for: ${measureTitle}`);
  try {
    if (isServer) {
      console.info(`🖥️ [setStart] Server-side measurement for: ${measureTitle}`);
      if (!global.measureTitles) {
        console.info('📝 [setStart] Initializing global.measureTitles');
        global.measureTitles = {};
      }
      if (!global.measureTitles[measureTitle]) {
        console.info(`📌 [setStart] Creating new measurement entry for: ${measureTitle}`);
        global.measureTitles[measureTitle] = {};
      }
      const timestamp = getTimestamp();
      global.measureTitles[measureTitle].startTime = timestamp;
      console.info(`⏰ [setStart] Set start time for ${measureTitle}: ${timestamp}`);
    } else {
      console.info(`🌐 [setStart] Client-side measurement for: ${measureTitle}`);
      addMeasure(measureTitle, 'startTime');
    }
  } catch (error) {
    console.error(`❌ [setStart] Error in setStart for ${measureTitle}:`, error);
    throw error;
  }
}

function addMeasure(measureTitle, unitTitle) {
  if (!devMode) return;
  console.info(`📊 [addMeasure] Adding ${unitTitle} for ${measureTitle}`);
  let measureTitles = {};

  try {
    // Check if localStorage is available
    if (typeof localStorage === 'undefined') {
      console.error('❌ [addMeasure] localStorage is not available');
      return;
    }

    // Initialize measureTitles in localStorage if it doesn't exist
    if (!localStorage.getItem('measureTitles')) {
      console.info('📝 [addMeasure] Initializing measureTitles in localStorage');
      localStorage.setItem('measureTitles', JSON.stringify({}));
    }

    // Parse the existing measureTitles
    try {
      measureTitles = JSON.parse(localStorage.getItem('measureTitles') || '{}');
      console.info(`📋 [addMeasure] Current measureTitles:`, measureTitles);
    } catch (e) {
      console.error('❌ [addMeasure] Error parsing measureTitles:', e);
      measureTitles = {};
    }
    // Initialize the measurement entry if it doesn't exist
    if (!measureTitles[measureTitle]) {
      console.info(`🆕 [addMeasure] Creating new entry for ${measureTitle}`);
      measureTitles[measureTitle] = { startTime: null, finishTime: null };
    }

    // Handle different types of measurements
    if (unitTitle === 'startTime' || unitTitle === 'finishTime') {
      const timestamp = getTimestamp();
      measureTitles[measureTitle][unitTitle] = timestamp;
      console.info(`⏰ [addMeasure] Set ${unitTitle} for ${measureTitle}: ${timestamp}`);
    } else if (unitTitle === 'timeDifference') {
      console.info('📐 [addMeasure] Calculating time difference');
      const storedData = JSON.parse(localStorage.getItem('measureTitles') || '{}');
      const startTime = storedData[measureTitle]?.startTime;
      const finishTime = storedData[measureTitle]?.finishTime;

      console.info(`⏱️ [addMeasure] Start time: ${startTime}`);
      console.info(`⏱️ [addMeasure] Finish time: ${finishTime}`);

      if (!startTime || !finishTime) {
        console.error(`❌ [addMeasure] Missing timestamps for ${measureTitle}:`, {
          startTime: !!startTime,
          finishTime: !!finishTime
        });
        return;
      }

      try {
        measureTitles[measureTitle][unitTitle] = getTimeDifferSeconds(
          finishTime,
          startTime
        );
        console.info(`✅ [addMeasure] Calculated time difference: ${measureTitles[measureTitle][unitTitle]}s`);
      } catch (error) {
        console.error(`❌ [addMeasure] Error calculating time difference:`, error);
      }
    }

    // Save the updated measurements back to localStorage
    try {
      localStorage.setItem('measureTitles', JSON.stringify(measureTitles));
      console.info('💾 [addMeasure] Successfully saved measurements to localStorage');
    } catch (e) {
      console.error('❌ [addMeasure] Error saving to localStorage:', e);
    }

  } catch (error) {
    console.error(`❌ [addMeasure] Unexpected error:`, error);
  }
}

function setFinish(measureTitle) {
  if (!devMode) return;
  console.info(`⏱️ [setFinish] Finishing measurement for: ${measureTitle}`);

  if (isServer) {
    console.info(`🖥️ [setFinish] Server-side finishing measurement`);
    if (!global.measureTitles || !global.measureTitles[measureTitle]) {
      console.error(`❌ [setFinish] No measurement started for ${measureTitle}`);
      return;
    }

    const timestamp = getTimestamp();
    global.measureTitles[measureTitle].finishTime = timestamp;
    console.info(`⏰ [setFinish] Set finish time: ${timestamp}`);

    const startTime = global.measureTitles[measureTitle].startTime;
    const finishTime = global.measureTitles[measureTitle].finishTime;

    try {
      const diff = getTimeDifferSeconds(finishTime, startTime);
      global.measureTitles[measureTitle].timeDifference = diff;
      console.info(`✅ [setFinish] Calculated time difference: ${diff}s`);
    } catch (error) {
      console.error(`❌ [setFinish] Error calculating time difference:`, error);
    }
  } else {
    console.info(`🌐 [setFinish] Client-side finishing measurement`);
    try {
      addMeasure(measureTitle, 'finishTime');
      addMeasure(measureTitle, 'timeDifference');
      console.info('📊 [setFinish] Added finish time and time difference measurements');
    } catch (error) {
      console.error('❌ [setFinish] Error in client-side measurement:', error);
    }
  }

  try {
    showResult(measureTitle);
  } catch (error) {
    console.error('❌ [setFinish] Error in showResult:', error);
  }
}

function showResult(measureTitle) {
  if (!devMode) return;
  console.info(`📊 [showResult] Showing results for: ${measureTitle}`);

  try {
    let timeDifference;
    let measurementData;

    if (isServer) {
      console.info('🖥️ [showResult] Server-side results');
      measurementData = global.measureTitles?.[measureTitle];
      timeDifference = measurementData?.timeDifference;

      console.info('📋 [showResult] Server measurement data:', {
        startTime: measurementData?.startTime,
        finishTime: measurementData?.finishTime,
        timeDifference
      });

      console.info('[Performance] 📊 Server-side measurement data:', {
        measureTitle,
        startTime: measurementData?.startTime,
        finishTime: measurementData?.finishTime,
        timeDifference
      });
    } else {
      const storedData = JSON.parse(localStorage.getItem('measureTitles') || '{}');
      measurementData = storedData?.[measureTitle];
      timeDifference = measurementData?.timeDifference;

      console.info('🌐 [showResult] Client-side measurement data:', {
        measureTitle,
        startTime: measurementData?.startTime,
        finishTime: measurementData?.finishTime,
        timeDifference
      });

      // Log the raw localStorage data for debugging
      try {
        const rawData = localStorage.getItem('measureTitles');
        console.info('📦 [showResult] Raw localStorage data:', rawData);
      } catch (e) {
        console.error('❌ [showResult] Error reading localStorage:', e);
      }
    }

    if (timeDifference === undefined) {
      console.warn(`⚠️ [showResult] No time difference recorded for ${measureTitle}`);
      console.info('🔍 [showResult] Available measurements:', isServer
        ? Object.keys(global.measureTitles || {})
        : Object.keys(JSON.parse(localStorage.getItem('measureTitles') || '{}'))
      );
      return;
    }

    // Format the time difference with appropriate units
    let formattedTime;
    if (timeDifference < 1) {
      formattedTime = `${(timeDifference * 1000).toFixed(2)}ms`;
    } else {
      formattedTime = `${timeDifference.toFixed(3)}s`;
    }

    // Log with emoji and formatting for better visibility
    const logMessage = `📈 ${measureTitle} took ${formattedTime}`;
    console.info(
      `%c[Performance] ${logMessage}`,
      'color: #4CAF50; font-weight: bold; background: #f5f5f5; padding: 2px 5px; border-radius: 3px;'
    );

    // Also log via uniLog
    console.info('📤 [showResult] Sending to uniLog:', measureTitle, formattedTime);
    uniLog(`[Performance] ${measureTitle}`, formattedTime);

    // Log a visual separator for better readability
    console.info('─'.repeat(80));

  } catch (error) {
    console.error(`❌ [showResult] Error for ${measureTitle}:`, error);
    console.error('🔍 [showResult] Error details:', {
      message: error.message,
      stack: error.stack
    });
  } finally {
    try {
      console.info('🗑️ [showResult] Cleaning up...');
      clearGarbage();
    } catch (e) {
      console.error('❌ [showResult] Error during cleanup:', e);
    }
  }
}

function clearGarbage() {
  if (!devMode) return;
  console.info('clearGarbage');
  if (isServer) {
    delete global.measureTitles;
  } else {
    localStorage.removeItem('measureTitles');
  }
}

export { startMeasuring, stopMeasuring, startMeasuringProd, stopMeasuringProd };
